import _ from 'lodash';
import { downloadFile, handleErrors } from '../../../utils/functions';
import { enqueueSnackbar } from 'notistack';
import { selectCurrentUser } from '../../../redux/slices/authSlice';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDownloadDocByIdMutation } from '../../../services/historyApi';
import {
    useDeleteDocumentByIdMutation,
    useGetEnabledDocsQuery,
    useLazyGetDocumentsQuery
} from '../../../services/documentsApi';
import useFilters from '../../../hooks/useFilters';

const ROWSPERPAGE = 10;

function useDocuments() {
    const user = useAppSelector(selectCurrentUser);
    const [savingDownload, setSavingDownload] = useState(false);
    const [documents, setDocuments] = useState<any>(null);
    const [tabValue, setTabValue] = useState(0);
    const [openDrawer, setOpenDrawer] = useState<number | undefined>(undefined);
    const [extraFilters, setExtraFilters] = useState<any>(null);

    const [saveDocumentDownload] = useDownloadDocByIdMutation();
    const [deleteDocumentById] = useDeleteDocumentByIdMutation();

    const {
        data: enabledDocs,
        isLoading: loadingEnabledDocs,
        isFetching: fetchingEnabledDocs
    } = useGetEnabledDocsQuery(undefined, {
        refetchOnMountOrArgChange: true
    });

    const [getDocuments, { isLoading: loadingDocuments, isFetching: fetchingDocuments }] =
        useLazyGetDocumentsQuery();

    const { filters, setFilters, handleChangeFilters, clearFilters } = useFilters({
        offset: 0,
        limit: ROWSPERPAGE,
        idTipoDocumento: -1
    });

    const defaultDocFilters: any = {
        offset: 0,
        limit: filters?.limit || ROWSPERPAGE
    };

    const getDocsIds = () => {
        const filteredEnabledDocs = enabledDocs?.filter((i: any) => i.descargar);
        return filteredEnabledDocs?.map((i: any) => i.tipoDocumento?.id).join(',');
    };

    const inputTypeDocOptions = useMemo(() => {
        //Filtrar opciones por valor de descarga en true
        const filteredEnabledDocs = enabledDocs?.filter((i: any) => i.descargar);

        const auxNewOptions = filteredEnabledDocs?.map((i: any) => ({ ...i.tipoDocumento }));

        if (auxNewOptions?.length > 1) {
            auxNewOptions?.unshift({ id: -1, nombre: 'Todos' });
        }

        return auxNewOptions;
    }, [enabledDocs]);

    useEffect(() => {
        if (!!documents) loadData(filters);
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        if (!documents && !!enabledDocs) loadData();
        // eslint-disable-next-line
    }, [documents, enabledDocs]);

    const loadData = async (filters?: any) => {
        let auxFilters: any = {};

        if (filters?.hasOwnProperty('offset')) {
            auxFilters = { ...filters };
        } else {
            auxFilters = { ...defaultDocFilters };
        }

        if (filters) {
            if (filters?.hasOwnProperty('idTipoDocumento')) {
                auxFilters = {
                    ...auxFilters,
                    ...filters,
                    idTipoDocumento:
                        filters.idTipoDocumento === -1 ? getDocsIds() : filters.idTipoDocumento
                };
            } else {
                auxFilters = { ...auxFilters, idTipoDocumento: getDocsIds(), ...filters };
            }
        } else {
            auxFilters = { ...auxFilters, idTipoDocumento: getDocsIds() };
        }

        const response: any = await getDocuments(auxFilters);

        if (!response?.error) {
            setDocuments(response?.data);
        } else {
            handleErrors(response?.error);
        }
    };

    const onTabChange = useCallback(
        (_: React.SyntheticEvent, newValue: number) => {
            const filterValue: { [key: number]: string | undefined } = {
                0: '',
                1: user?.cuil
            };
            setTabValue(newValue);
            if (inputTypeDocOptions) {
                if (newValue === 0) {
                    handleClearFilters();
                } else if (newValue === 1) {
                    setFilters({
                        ...filters,
                        cuilOperador: filterValue[newValue]
                    });
                }
            }
        },
        // eslint-disable-next-line
        [user, inputTypeDocOptions, filters]
    );

    const handleClearFilters = () => {
        clearFilters({ ...defaultDocFilters, idTipoDocumento: -1 });
        setExtraFilters(null);
        if (tabValue === 1) {
            setTabValue(0);
        }
        loadData();
    };

    const handleOpenDrawer = useCallback((docId: any) => {
        setOpenDrawer(docId);
    }, []);

    const handleCloseDrawer = () => {
        setOpenDrawer(undefined);
    };

    const handleChangeExtraFilters = useCallback(
        (event: any) => {
            const { name, value } = event?.target;
            if (!value) {
                let auxFilters = { ...extraFilters };
                delete auxFilters?.[name];
                setExtraFilters(auxFilters);
            } else {
                setExtraFilters({
                    ...extraFilters,
                    [name]: value
                });
            }
        },
        // eslint-disable-next-line
        [extraFilters, filters]
    );

    const getStringDate = (date: Date) => {
        const [month, day, year] = [date.getMonth(), date.getDate(), date.getFullYear()];
        return `${year}/${month + 1}/${day}`;
    };

    const handleSearch = useCallback(async () => {
        const fechaHasta = new Date(extraFilters.fechaHasta);
        const fechaDesde = new Date(extraFilters.fechaDesde);
        // return console.log('FILTROS', getStringDate(fechaDesde), getStringDate(fechaHasta));
        let auxFilters = { ...extraFilters };

        if (tabValue === 1) {
            auxFilters.cuilOperador = user?.cuil;
        }

        auxFilters.fechaDesde = getStringDate(fechaDesde);
        auxFilters.fechaHasta = getStringDate(fechaHasta);
        auxFilters.criterioOrden = 'fechaCreacion';

        setFilters({
            ...defaultDocFilters,
            ...auxFilters
        });
        // eslint-disable-next-line
    }, [filters, extraFilters]);

    const disabledFilters = useMemo(() => {
        return !extraFilters?.cuilCuit && !extraFilters?.referenciaAdicionalPropietario;
    }, [extraFilters]);

    const handleDownloadDocument = useCallback(
        async (item: any) => {
            try {
                setSavingDownload(true);
                await fetch(item?.row?.url)
                    .then((response) => response.blob())
                    .then((blob) => {
                        const name = item?.row?.nombre;
                        downloadFile({ blob, name });
                    })
                    .catch((error) => {
                        enqueueSnackbar('Ocurrió un problema', {
                            variant: 'error'
                        });
                    });
                await saveDocumentDownload({
                    tipoDocumento: item?.row?.tipoDocumento,
                    idDocumento: item?.row?.idDocumento
                });
            } catch (e) {
                handleErrors(e);
            } finally {
                setSavingDownload(false);
            }
        },
        [saveDocumentDownload]
    );

    const handleDeleteDocument = useCallback(
        async (item: any) => {
            try {
                await deleteDocumentById({ docId: item.id });
            } catch (e) {
                handleErrors(e);
            }
        },
        [deleteDocumentById]
    );

    const dataTableLoading = useMemo(() => {
        return loadingDocuments || fetchingDocuments || loadingEnabledDocs || fetchingEnabledDocs;
    }, [loadingDocuments, fetchingDocuments, loadingEnabledDocs, fetchingEnabledDocs]);

    return {
        documents,
        loadingDocuments,
        fetchingDocuments,
        openDrawer,
        disabledFilters,
        enabledDocs,
        loadingEnabledDocs,
        fetchingEnabledDocs,
        inputTypeDocOptions,
        filters,
        dataTableLoading,
        tabValue,
        savingDownload,
        extraFilters,
        setFilters,
        handleChangeExtraFilters,
        handleDownloadDocument,
        handleClearFilters,
        handleChangeFilters,
        clearFilters,
        handleCloseDrawer,
        handleOpenDrawer,
        onTabChange,
        handleSearch,
        handleDeleteDocument
    };
}

export default useDocuments;
